import {
    useDrawdownRepaymentSchedule,
    useGetFormalOffer
} from '@api/lendingAPI/lapiHooks';
import { tracking } from '@iwoca/frontend-tracking-library';
import { IconAngleDown, IconPenFilled } from '@iwoca/icons';
import { Accordion, IwocaLogo, Text } from '@iwoca/orion';
import { getCurrentLocation } from '@iwRouter/HistoryProvider';
import { getQueryParameter, maintainStateKey } from '@iwRouter/LocationHelper';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';

import { FormattedCurrency } from '../../../../../iwIntlProvider/FormattedCurrency';
import { TrustPilot } from '../../../../TrustPilot/TrustPilot';
import { getNonInterestOnlyPaymentAmount } from '../../../MyOffers.helpers';
import {
    FullTermDurationRow,
    InterestRateRow,
    WithdrawalAmountRow,
    WithdrawalFeesRow
} from '../../ApprovedView/LoanSummary/SummaryRow/SummaryRow';
import { NeedHelpSection } from '../../components/NeedHelpSection/NeedHelpSection';

export const FundingRequirementsLoanSummary = () => {
    const location = getCurrentLocation();
    const offerId = getQueryParameter(location, 'offerId');
    const amount = getQueryParameter(location, 'amount');
    const drawdownAmount = amount ? Number(amount) : undefined;

    const { formalOffer } = useGetFormalOffer(offerId);
    const selectedOffer = formalOffer ?? undefined;

    const { data } = useDrawdownRepaymentSchedule({
        offerId: offerId ?? '',
        drawdownAmount: drawdownAmount ?? 0,
        enabled: Boolean(offerId) && Boolean(drawdownAmount)
    });

    let monthlyRepayment: number | null = null;
    if (data) {
        if (data.summary.is_equal_repayment) {
            monthlyRepayment =
                data.summary.simplified_schedule?.equal_repayments?.amount ??
                null;
        } else {
            monthlyRepayment =
                getNonInterestOnlyPaymentAmount(data.schedule) ?? null;
        }
    }

    return (
        <>
            <div className="flex items-center justify-between">
                <Text className="text-2xl font-bold">
                    <FormattedMessage
                        id="_.funding_requirements.loan_summary.title"
                        defaultMessage="Your loan summary"
                    />
                </Text>
                <Link
                    to={maintainStateKey({ pathname: '/my-offers/' })}
                    onClick={() =>
                        tracking.ampli.fundingRequirementsLoanSummaryBackClicked(
                            { version: '2.0' }
                        )
                    }
                    className="flex h-[32px] w-[32px] items-center justify-center rounded-full border border-structure-100"
                >
                    <IconPenFilled className="h-l w-l" />
                </Link>
            </div>

            <div>
                <Text className="text-m font-reg">
                    <FormattedMessage
                        id="_.funding_requirements.loan_summary.min_monthly_repayment"
                        defaultMessage="Min. monthly repayment"
                    />
                </Text>

                <Text className="text-m font-med">
                    <FormattedMessage
                        id="_.funding_requirements.loan_summary.monthly_amount"
                        defaultMessage="{amount} per month"
                        values={{
                            amount: (
                                <span className="text-3xl font-bold">
                                    {monthlyRepayment !== null ? (
                                        <FormattedCurrency
                                            value={monthlyRepayment}
                                        />
                                    ) : (
                                        '£--.--'
                                    )}
                                </span>
                            )
                        }}
                    />
                </Text>
            </div>

            <div>
                <div className="flex flex-col gap-m border-t border-secondary-20 pb-2xl pt-xl">
                    <WithdrawalAmountRow drawdownAmount={drawdownAmount} />
                    <InterestRateRow
                        selectedOffer={selectedOffer}
                        drawdownAmount={drawdownAmount}
                    />
                    <WithdrawalFeesRow selectedOffer={selectedOffer} />
                </div>

                <FullTermDurationRow
                    selectedOffer={selectedOffer}
                    drawdownAmount={drawdownAmount}
                />
            </div>

            <div className="-ml-m mt-xl">
                <Accordion.Item variant="plain" size="small">
                    <Accordion.Header>
                        <Accordion.Title className="text-s font-bold">
                            <FormattedMessage
                                id="_.funding_requirements.loan_summary.still_need_help"
                                defaultMessage="Still need help?"
                            />
                        </Accordion.Title>
                        <IconAngleDown className="text-l" />
                    </Accordion.Header>
                    <Accordion.Content>
                        <NeedHelpSection variant="funding-desktop" />
                    </Accordion.Content>
                </Accordion.Item>
            </div>

            <div className="mt-auto flex flex-col items-center pt-xl text-center">
                <IwocaLogo className="mb-xl w-[100px]" color="white" />
                <TrustPilot theme="dark" />
            </div>
        </>
    );
};
